// Lightweight i18n dictionary for the BillPortal marketing site.
// English is the source of truth; ES/FR/DE cover navigation, hero, section
// headers, CTAs, footer and forms. Long-form content (blog/case-study bodies)
// is authored in English in data/content.js and falls back gracefully.

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
];

export const translations = {
  en: {
    nav: {
      platform: 'Platform',
      pricing: 'Pricing',
      solutions: 'Solutions',
      caseStudies: 'Case Studies',
      blog: 'Blog',
      resources: 'Resources',
      company: 'Company',
      howItWorks: 'How it works',
      about: 'About',
      signIn: 'Sign in',
      requestDemo: 'Request a demo',
      contact: 'Contact',
      multifamily: 'Multifamily',
      commercial: 'Commercial & Industrial',
      brokers: 'Energy Brokers',
    },
    common: {
      requestDemo: 'Request a demo',
      talkToSales: 'Talk to sales',
      getStarted: 'Get started',
      learnMore: 'Learn more',
      explore: 'Explore',
      readStory: 'Read the story',
      readArticle: 'Read article',
      viewAll: 'View all',
      allCaseStudies: 'All case studies',
      allArticles: 'All articles',
      back: 'Back',
      submit: 'Submit',
      sending: 'Sending…',
      by: 'By',
      minRead: 'min read',
      lightMode: 'Light',
      darkMode: 'Dark',
      language: 'Language',
      share: 'Share',
    },
    hero: {
      badge: 'AI-Supported Utility Bill & Expense Management',
      title: 'Take control of your',
      titleHighlight: 'utilities',
      titleEnd: '— collected, validated and paid.',
      subtitle:
        'People and AI Agents in one workspace — collecting, validating and paying every utility bill, then turning the data into financial, operational and sustainability insight. Every step, a complete audit trail.',
      ctaPrimary: 'Request a demo',
      ctaSecondary: 'See the platform',
      ctaTry: 'Try Bill Extraction',
      ctaTryParser: 'Try Bill Extraction',
      trustedBy: 'Purpose-built for multifamily, commercial & industrial, and energy brokers',
    },
    home: {
      problem: {
        eyebrow: 'The problem',
        title: 'Utility spend is high-volume, fragmented and error-prone',
        subtitle:
          'Bills arrive from dozens of suppliers across portals, email and post. Missing invoices, late fees and manual approvals drain finance teams and hide data you need for payments, budgets and sustainability reporting.',
      },
      platform: {
        eyebrow: 'The platform',
        title: 'One workspace for the entire utility lifecycle',
        subtitle:
          'From acquisition to payment to reporting — a single, auditable source of truth for every bill and every dollar.',
      },
      segments: {
        eyebrow: 'Who we serve',
        title: 'Tailored solutions for how you operate',
        subtitle:
          'Whether you manage thousands of units, a global property portfolio, or a book of energy clients, BillPortal adapts to your model.',
      },
      channels: {
        eyebrow: 'Bill acquisition',
        title: 'Collect bills from every source — automatically',
        subtitle:
          'Acquisition bots, inboxes, uploads, SFTP and scanned mail flow into one secure, centralised repository.',
      },
      payments: {
        eyebrow: 'Payments',
        title: 'Pay on time, every time — through our banking partner',
        subtitle:
          'Verify and consolidate bills into payment files with GL and cost-code allocation, then pay by ACH, card or cheque with remittance attached.',
      },
      reporting: {
        eyebrow: 'Data & reporting',
        title: 'Audit-ready data for finance and sustainability',
        subtitle:
          'Emissions accounting, ENERGY STAR® Portfolio Manager compliance and completeness reporting — powered by the same validated data.',
      },
      integrations: {
        eyebrow: 'Integrations',
        title: 'Connected to the systems you already run',
        subtitle:
          'Two-way sync with ERP/AP, sustainability platforms and energy systems keeps every team aligned.',
      },
      testimonials: {
        eyebrow: 'Testimonials',
        title: 'What teams are saying',
      },
      cta: {
        title: 'See how much time and spend you can reclaim',
        subtitle:
          'Book a personalised walkthrough and we’ll map BillPortal to your portfolio, suppliers and payment flows.',
        primary: 'Request a demo',
        secondary: 'Talk to sales',
      },
    },
    segments: {
      multifamily: {
        name: 'Multifamily',
        tagline: 'Utility Expense Management & Vacant Cost Recovery for residential portfolios.',
      },
      commercial: {
        name: 'Commercial & Industrial',
        tagline: 'Centralised bill data, payment files and environmental reporting at global scale.',
      },
      brokers: {
        name: 'Energy Brokers',
        tagline: 'A data foundation for contracting, budgeting and proactive energy management.',
      },
    },
    footer: {
      tagline: 'The AI-powered workspace for utility bill and expense management.',
      product: 'Product',
      solutions: 'Solutions',
      company: 'Company',
      resources: 'Resources',
      legal: 'Legal',
      subscribe: 'Stay ahead of your utility data',
      subscribeSub: 'Monthly insights on utility expense management, payments and sustainability. No spam.',
      subscribePlaceholder: 'Work email',
      subscribeCta: 'Subscribe',
      subscribed: 'Subscribed — thank you!',
      rights: 'All rights reserved.',
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
      sla: 'SLA',
      dpa: 'DPA',
      security: 'Security',
      disclaimer:
        'Case studies are anonymised. Figures are representative of platform-managed portfolios and do not identify any client.',
    },
    form: {
      name: 'Full name',
      email: 'Work email',
      company: 'Company',
      phone: 'Phone',
      jobTitle: 'Job title',
      segment: 'Which best describes you?',
      portfolioSize: 'Portfolio size',
      interest: 'What are you most interested in?',
      message: 'How can we help?',
      selectOption: 'Select an option',
      namePh: 'Jane Doe',
      emailPh: 'jane@company.com',
      companyPh: 'Company name',
      phonePh: '+1 (555) 000-0000',
      messagePh: 'Tell us about your portfolio, suppliers and goals…',
      consent: 'By submitting, you agree to be contacted about BillPortal. We respect your privacy.',
      successTitle: 'Thank you — request received',
      successBody: 'Our team will reach out within one business day to schedule your walkthrough.',
      errorBody: 'Something went wrong. Please check your details and try again.',
      submitDemo: 'Request my demo',
      submitContact: 'Send message',
    },
    demo: {
      title: 'Request a personalised demo',
      subtitle:
        'See BillPortal mapped to your portfolio — acquisition, validation, payments and reporting in one workspace.',
      benefits: [
        'A walkthrough tailored to your segment and suppliers',
        'Live look at AI parsing, validation and payment files',
        'Guidance on onboarding, historical data and go-live',
      ],
    },
  },

  es: {
    nav: {
      platform: 'Plataforma',
      pricing: 'Precios',
      solutions: 'Soluciones',
      caseStudies: 'Casos de éxito',
      blog: 'Blog',
      resources: 'Recursos',
      company: 'Empresa',
      howItWorks: 'Cómo funciona',
      about: 'Nosotros',
      signIn: 'Iniciar sesión',
      requestDemo: 'Solicitar demo',
      contact: 'Contacto',
      multifamily: 'Multifamiliar',
      commercial: 'Comercial e Industrial',
      brokers: 'Brókers de energía',
    },
    common: {
      requestDemo: 'Solicitar demo',
      talkToSales: 'Hablar con ventas',
      getStarted: 'Comenzar',
      learnMore: 'Saber más',
      explore: 'Explorar',
      readStory: 'Leer el caso',
      readArticle: 'Leer artículo',
      viewAll: 'Ver todo',
      allCaseStudies: 'Todos los casos',
      allArticles: 'Todos los artículos',
      back: 'Volver',
      submit: 'Enviar',
      sending: 'Enviando…',
      by: 'Por',
      minRead: 'min de lectura',
      lightMode: 'Claro',
      darkMode: 'Oscuro',
      language: 'Idioma',
      share: 'Compartir',
    },
    hero: {
      badge: 'Gestión de facturas y gastos de servicios con IA',
      title: 'Cada factura de servicios,',
      titleHighlight: 'recopilada, validada y pagada',
      titleEnd: 'en una sola plataforma.',
      subtitle:
        'BillPortal automatiza todo el ciclo de los servicios: recopilación de facturas desde cualquier origen, extracción de datos con IA, validación, financiación y pagos, para que finanzas y operaciones pasen del trabajo manual al control por excepción.',
      ctaPrimary: 'Solicitar demo',
      ctaSecondary: 'Ver la plataforma',
      ctaTry: 'Probar extracción de facturas',
      ctaTryParser: 'Probar extracción de facturas',
      trustedBy: 'Diseñada para multifamiliar, comercial e industrial, y brókers de energía',
    },
    home: {
      problem: {
        eyebrow: 'El problema',
        title: 'El gasto en servicios es masivo, fragmentado y propenso a errores',
        subtitle:
          'Las facturas llegan de decenas de proveedores por portales, correo y correspondencia. Facturas faltantes, recargos por mora y aprobaciones manuales agotan a los equipos y ocultan datos clave para pagos, presupuestos y sostenibilidad.',
      },
      platform: {
        eyebrow: 'La plataforma',
        title: 'Un único espacio para todo el ciclo de servicios',
        subtitle:
          'De la adquisición al pago y al reporte: una única fuente de verdad auditable para cada factura y cada dólar.',
      },
      segments: {
        eyebrow: 'A quién servimos',
        title: 'Soluciones a la medida de tu operación',
        subtitle:
          'Ya sea que gestiones miles de unidades, un portafolio global o una cartera de clientes de energía, BillPortal se adapta a tu modelo.',
      },
      channels: {
        eyebrow: 'Adquisición de facturas',
        title: 'Recopila facturas de cualquier origen, automáticamente',
        subtitle:
          'Bots de adquisición, buzones, cargas, SFTP y correo escaneado fluyen a un repositorio central y seguro.',
      },
      payments: {
        eyebrow: 'Pagos',
        title: 'Paga a tiempo, siempre, con nuestro socio bancario',
        subtitle:
          'Verifica y consolida facturas en archivos de pago con asignación de cuentas y códigos, y paga por ACH, tarjeta o cheque con la remesa adjunta.',
      },
      reporting: {
        eyebrow: 'Datos e informes',
        title: 'Datos listos para auditoría: finanzas y sostenibilidad',
        subtitle:
          'Contabilidad de emisiones, cumplimiento con ENERGY STAR® Portfolio Manager e informes de completitud, con los mismos datos validados.',
      },
      integrations: {
        eyebrow: 'Integraciones',
        title: 'Conectado a los sistemas que ya usas',
        subtitle:
          'La sincronización bidireccional con ERP/AP, plataformas de sostenibilidad y sistemas de energía mantiene a todos alineados.',
      },
      testimonials: {
        eyebrow: 'Testimonios',
        title: 'Lo que dicen los equipos',
      },
      cta: {
        title: 'Descubre cuánto tiempo y gasto puedes recuperar',
        subtitle:
          'Reserva una demostración personalizada y mapearemos BillPortal a tu portafolio, proveedores y flujos de pago.',
        primary: 'Solicitar demo',
        secondary: 'Hablar con ventas',
      },
    },
    segments: {
      multifamily: {
        name: 'Multifamiliar',
        tagline: 'Gestión de gastos de servicios y recuperación de costos de vacancia para portafolios residenciales.',
      },
      commercial: {
        name: 'Comercial e Industrial',
        tagline: 'Datos centralizados de facturas, archivos de pago e informes ambientales a escala global.',
      },
      brokers: {
        name: 'Brókers de energía',
        tagline: 'Una base de datos para contratación, presupuesto y gestión proactiva de la energía.',
      },
    },
    footer: {
      tagline: 'El espacio de trabajo con IA para la gestión de facturas y gastos de servicios.',
      product: 'Producto',
      solutions: 'Soluciones',
      company: 'Empresa',
      resources: 'Recursos',
      legal: 'Legal',
      subscribe: 'Mantente al día con tus datos de servicios',
      subscribeSub: 'Ideas mensuales sobre gestión de gastos, pagos y sostenibilidad. Sin spam.',
      subscribePlaceholder: 'Correo de trabajo',
      subscribeCta: 'Suscribirme',
      subscribed: 'Suscrito, ¡gracias!',
      rights: 'Todos los derechos reservados.',
      privacy: 'Privacidad',
      terms: 'Términos',
      cookies: 'Cookies',
      sla: 'SLA',
      dpa: 'DPA',
      security: 'Seguridad',
      disclaimer:
        'Los casos de éxito son anónimos. Las cifras son representativas de portafolios gestionados y no identifican a ningún cliente.',
    },
    form: {
      name: 'Nombre completo',
      email: 'Correo de trabajo',
      company: 'Empresa',
      phone: 'Teléfono',
      jobTitle: 'Cargo',
      segment: '¿Qué te describe mejor?',
      portfolioSize: 'Tamaño del portafolio',
      interest: '¿Qué te interesa más?',
      message: '¿Cómo podemos ayudarte?',
      selectOption: 'Selecciona una opción',
      namePh: 'Juana Pérez',
      emailPh: 'juana@empresa.com',
      companyPh: 'Nombre de la empresa',
      phonePh: '+1 (555) 000-0000',
      messagePh: 'Cuéntanos sobre tu portafolio, proveedores y objetivos…',
      consent: 'Al enviar, aceptas ser contactado sobre BillPortal. Respetamos tu privacidad.',
      successTitle: 'Gracias, solicitud recibida',
      successBody: 'Nuestro equipo te contactará en un día hábil para agendar tu demostración.',
      errorBody: 'Algo salió mal. Revisa tus datos e inténtalo de nuevo.',
      submitDemo: 'Solicitar mi demo',
      submitContact: 'Enviar mensaje',
    },
    demo: {
      title: 'Solicita una demo personalizada',
      subtitle:
        'Ve BillPortal aplicado a tu portafolio: adquisición, validación, pagos e informes en un solo espacio.',
      benefits: [
        'Una demostración adaptada a tu segmento y proveedores',
        'Vista en vivo de parsing con IA, validación y archivos de pago',
        'Orientación sobre onboarding, datos históricos y puesta en marcha',
      ],
    },
  },

  fr: {
    nav: {
      platform: 'Plateforme',
      pricing: 'Tarifs',
      solutions: 'Solutions',
      caseStudies: 'Études de cas',
      blog: 'Blog',
      resources: 'Ressources',
      company: 'Entreprise',
      howItWorks: 'Comment ça marche',
      about: 'À propos',
      signIn: 'Connexion',
      requestDemo: 'Demander une démo',
      contact: 'Contact',
      multifamily: 'Résidentiel collectif',
      commercial: 'Commercial et industriel',
      brokers: 'Courtiers en énergie',
    },
    common: {
      requestDemo: 'Demander une démo',
      talkToSales: 'Parler aux ventes',
      getStarted: 'Commencer',
      learnMore: 'En savoir plus',
      explore: 'Explorer',
      readStory: 'Lire le cas',
      readArticle: 'Lire l’article',
      viewAll: 'Tout voir',
      allCaseStudies: 'Toutes les études',
      allArticles: 'Tous les articles',
      back: 'Retour',
      submit: 'Envoyer',
      sending: 'Envoi…',
      by: 'Par',
      minRead: 'min de lecture',
      lightMode: 'Clair',
      darkMode: 'Sombre',
      language: 'Langue',
      share: 'Partager',
    },
    hero: {
      badge: 'Gestion des factures et dépenses d’énergie assistée par IA',
      title: 'Prenez le contrôle de vos',
      titleHighlight: 'services',
      titleEnd: '— collectés, validés et payés.',
      subtitle:
        'Allez au-delà du simple repérage des factures manquantes et des échéances dépassées : passez à l’action, avec une piste d’audit complète. Les Collection Agents couvrent la plupart des portails de services aux États-Unis et les paiements groupés règlent vos fournisseurs à temps. C’est en self-service, mais des Agents IA orchestrent le travail avec des humains dans la boucle — et nous proposons un service entièrement géré si vous préférez nous le confier.',
      ctaPrimary: 'Demander une démo',
      ctaSecondary: 'Voir la plateforme',
      ctaTry: 'Tester l’extraction de factures',
      ctaTryParser: 'Tester l’extraction de factures',
      trustedBy: 'Conçu pour le résidentiel collectif, le commercial et industriel et les courtiers en énergie',
    },
    home: {
      problem: {
        eyebrow: 'Le problème',
        title: 'Les dépenses d’énergie sont volumineuses, fragmentées et sources d’erreurs',
        subtitle:
          'Les factures arrivent de dizaines de fournisseurs via portails, e-mail et courrier. Factures manquantes, pénalités de retard et validations manuelles épuisent les équipes et masquent des données clés.',
      },
      platform: {
        eyebrow: 'La plateforme',
        title: 'Un seul espace pour tout le cycle des services',
        subtitle:
          'De la collecte au paiement et au reporting : une source de vérité unique et auditable pour chaque facture et chaque euro.',
      },
      segments: {
        eyebrow: 'Nos clients',
        title: 'Des solutions adaptées à votre fonctionnement',
        subtitle:
          'Que vous gériez des milliers de logements, un portefeuille immobilier mondial ou un portefeuille de clients énergie, BillPortal s’adapte.',
      },
      channels: {
        eyebrow: 'Collecte des factures',
        title: 'Collectez les factures de toute source, automatiquement',
        subtitle:
          'Bots de collecte, boîtes mail, dépôts, SFTP et courrier numérisé alimentent un référentiel central et sécurisé.',
      },
      payments: {
        eyebrow: 'Paiements',
        title: 'Payez à temps, à chaque fois, via notre partenaire bancaire',
        subtitle:
          'Vérifiez et consolidez les factures en fichiers de paiement avec affectation comptable, puis payez par ACH, carte ou chèque avec l’avis de paiement joint.',
      },
      reporting: {
        eyebrow: 'Données et reporting',
        title: 'Des données prêtes pour l’audit : finance et durabilité',
        subtitle:
          'Comptabilité des émissions, conformité ENERGY STAR® Portfolio Manager et rapports de complétude — à partir des mêmes données validées.',
      },
      integrations: {
        eyebrow: 'Intégrations',
        title: 'Connecté aux systèmes que vous utilisez déjà',
        subtitle:
          'La synchronisation bidirectionnelle avec ERP/AP, plateformes de durabilité et systèmes énergétiques garde chaque équipe alignée.',
      },
      testimonials: {
        eyebrow: 'Témoignages',
        title: 'Ce que disent les équipes',
      },
      cta: {
        title: 'Voyez le temps et les dépenses que vous pouvez récupérer',
        subtitle:
          'Réservez une démonstration personnalisée : nous mapperons BillPortal à votre portefeuille, vos fournisseurs et vos flux de paiement.',
        primary: 'Demander une démo',
        secondary: 'Parler aux ventes',
      },
    },
    segments: {
      multifamily: {
        name: 'Résidentiel collectif',
        tagline: 'Gestion des dépenses d’énergie et récupération des coûts de vacance pour portefeuilles résidentiels.',
      },
      commercial: {
        name: 'Commercial et industriel',
        tagline: 'Données de factures centralisées, fichiers de paiement et reporting environnemental à l’échelle mondiale.',
      },
      brokers: {
        name: 'Courtiers en énergie',
        tagline: 'Un socle de données pour la contractualisation, le budget et la gestion proactive de l’énergie.',
      },
    },
    footer: {
      tagline: 'L’espace de travail IA pour la gestion des factures et dépenses d’énergie.',
      product: 'Produit',
      solutions: 'Solutions',
      company: 'Entreprise',
      resources: 'Ressources',
      legal: 'Mentions légales',
      subscribe: 'Gardez une longueur d’avance sur vos données',
      subscribeSub: 'Des analyses mensuelles sur la gestion des dépenses, les paiements et la durabilité. Pas de spam.',
      subscribePlaceholder: 'E-mail professionnel',
      subscribeCta: 'S’abonner',
      subscribed: 'Inscrit, merci !',
      rights: 'Tous droits réservés.',
      privacy: 'Confidentialité',
      terms: 'Conditions',
      cookies: 'Cookies',
      sla: 'SLA',
      dpa: 'DPA',
      security: 'Sécurité',
      disclaimer:
        'Les études de cas sont anonymisées. Les chiffres sont représentatifs de portefeuilles gérés et n’identifient aucun client.',
    },
    form: {
      name: 'Nom complet',
      email: 'E-mail professionnel',
      company: 'Société',
      phone: 'Téléphone',
      jobTitle: 'Fonction',
      segment: 'Qu’est-ce qui vous décrit le mieux ?',
      portfolioSize: 'Taille du portefeuille',
      interest: 'Qu’est-ce qui vous intéresse le plus ?',
      message: 'Comment pouvons-nous aider ?',
      selectOption: 'Choisir une option',
      namePh: 'Jeanne Dupont',
      emailPh: 'jeanne@societe.com',
      companyPh: 'Nom de la société',
      phonePh: '+1 (555) 000-0000',
      messagePh: 'Parlez-nous de votre portefeuille, vos fournisseurs et vos objectifs…',
      consent: 'En envoyant, vous acceptez d’être contacté au sujet de BillPortal. Nous respectons votre vie privée.',
      successTitle: 'Merci — demande reçue',
      successBody: 'Notre équipe vous contactera sous un jour ouvré pour planifier votre démonstration.',
      errorBody: 'Une erreur est survenue. Vérifiez vos informations et réessayez.',
      submitDemo: 'Demander ma démo',
      submitContact: 'Envoyer le message',
    },
    demo: {
      title: 'Demandez une démo personnalisée',
      subtitle:
        'Découvrez BillPortal appliqué à votre portefeuille : collecte, validation, paiements et reporting en un seul espace.',
      benefits: [
        'Une démonstration adaptée à votre segment et vos fournisseurs',
        'Aperçu en direct du parsing IA, de la validation et des fichiers de paiement',
        'Conseils sur l’onboarding, les données historiques et la mise en service',
      ],
    },
  },

  de: {
    nav: {
      platform: 'Plattform',
      pricing: 'Preise',
      solutions: 'Lösungen',
      caseStudies: 'Fallstudien',
      blog: 'Blog',
      resources: 'Ressourcen',
      company: 'Unternehmen',
      howItWorks: 'So funktioniert’s',
      about: 'Über uns',
      signIn: 'Anmelden',
      requestDemo: 'Demo anfragen',
      contact: 'Kontakt',
      multifamily: 'Mehrfamilien',
      commercial: 'Gewerbe & Industrie',
      brokers: 'Energie-Makler',
    },
    common: {
      requestDemo: 'Demo anfragen',
      talkToSales: 'Vertrieb kontaktieren',
      getStarted: 'Loslegen',
      learnMore: 'Mehr erfahren',
      explore: 'Entdecken',
      readStory: 'Fallstudie lesen',
      readArticle: 'Artikel lesen',
      viewAll: 'Alle ansehen',
      allCaseStudies: 'Alle Fallstudien',
      allArticles: 'Alle Artikel',
      back: 'Zurück',
      submit: 'Senden',
      sending: 'Senden…',
      by: 'Von',
      minRead: 'Min. Lesezeit',
      lightMode: 'Hell',
      darkMode: 'Dunkel',
      language: 'Sprache',
      share: 'Teilen',
    },
    hero: {
      badge: 'KI-unterstütztes Versorgungsrechnungs- & Ausgabenmanagement',
      title: 'Übernehmen Sie die Kontrolle über Ihre',
      titleHighlight: 'Versorgung',
      titleEnd: '— erfasst, validiert und bezahlt.',
      subtitle:
        'Gehen Sie über das bloße Erkennen fehlender Rechnungen und überfälliger Termine hinaus — handeln Sie, mit einem vollständigen Prüfpfad. Collection Agents decken die meisten US-Versorgungsportale ab und Sammelzahlungen begleichen Ihre Lieferanten pünktlich. Es ist Self-Service, aber KI-Agenten orchestrieren die Arbeit mit Menschen in der Schleife — und wir bieten einen vollständig verwalteten Service, wenn Sie es uns lieber überlassen.',
      ctaPrimary: 'Demo anfragen',
      ctaSecondary: 'Plattform ansehen',
      ctaTry: 'Rechnungsextraktion testen',
      ctaTryParser: 'Rechnungsextraktion testen',
      trustedBy: 'Entwickelt für Mehrfamilien, Gewerbe & Industrie und Energie-Makler',
    },
    home: {
      problem: {
        eyebrow: 'Das Problem',
        title: 'Versorgungsausgaben sind umfangreich, fragmentiert und fehleranfällig',
        subtitle:
          'Rechnungen kommen von Dutzenden Anbietern über Portale, E-Mail und Post. Fehlende Rechnungen, Mahngebühren und manuelle Freigaben belasten Teams und verbergen wichtige Daten.',
      },
      platform: {
        eyebrow: 'Die Plattform',
        title: 'Ein Arbeitsbereich für den gesamten Versorgungs-Lebenszyklus',
        subtitle:
          'Von der Erfassung über die Zahlung bis zum Reporting: eine einzige, prüfbare Quelle der Wahrheit für jede Rechnung und jeden Euro.',
      },
      segments: {
        eyebrow: 'Für wen wir arbeiten',
        title: 'Maßgeschneiderte Lösungen für Ihre Arbeitsweise',
        subtitle:
          'Ob Sie tausende Einheiten, ein globales Portfolio oder ein Buch an Energiekunden verwalten — BillPortal passt sich an.',
      },
      channels: {
        eyebrow: 'Rechnungserfassung',
        title: 'Erfassen Sie Rechnungen aus jeder Quelle — automatisch',
        subtitle:
          'Erfassungs-Bots, Postfächer, Uploads, SFTP und gescannte Post fließen in ein sicheres, zentrales Repository.',
      },
      payments: {
        eyebrow: 'Zahlungen',
        title: 'Pünktlich zahlen — über unseren Bankpartner',
        subtitle:
          'Rechnungen prüfen und in Zahlungsdateien mit Konten- und Kostenstellenzuordnung bündeln, dann per ACH, Karte oder Scheck mit Zahlungsavis bezahlen.',
      },
      reporting: {
        eyebrow: 'Daten & Reporting',
        title: 'Prüfsichere Daten für Finance und Nachhaltigkeit',
        subtitle:
          'Emissionsbilanzierung, ENERGY STAR® Portfolio Manager-Compliance und Vollständigkeitsberichte — aus denselben validierten Daten.',
      },
      integrations: {
        eyebrow: 'Integrationen',
        title: 'Verbunden mit Ihren bestehenden Systemen',
        subtitle:
          'Bidirektionale Synchronisierung mit ERP/AP, Nachhaltigkeits- und Energiesystemen hält jedes Team im Einklang.',
      },
      testimonials: {
        eyebrow: 'Stimmen',
        title: 'Was Teams sagen',
      },
      cta: {
        title: 'Sehen Sie, wie viel Zeit und Ausgaben Sie zurückgewinnen',
        subtitle:
          'Buchen Sie eine persönliche Demo — wir bilden BillPortal auf Ihr Portfolio, Ihre Anbieter und Zahlungsflüsse ab.',
        primary: 'Demo anfragen',
        secondary: 'Vertrieb kontaktieren',
      },
    },
    segments: {
      multifamily: {
        name: 'Mehrfamilien',
        tagline: 'Versorgungsausgaben-Management und Leerstands-Kostenrückgewinnung für Wohnportfolios.',
      },
      commercial: {
        name: 'Gewerbe & Industrie',
        tagline: 'Zentralisierte Rechnungsdaten, Zahlungsdateien und Umweltreporting im globalen Maßstab.',
      },
      brokers: {
        name: 'Energie-Makler',
        tagline: 'Eine Datenbasis für Vertragsabschluss, Budgetierung und proaktives Energiemanagement.',
      },
    },
    footer: {
      tagline: 'Der KI-gestützte Arbeitsbereich für Versorgungsrechnungs- und Ausgabenmanagement.',
      product: 'Produkt',
      solutions: 'Lösungen',
      company: 'Unternehmen',
      resources: 'Ressourcen',
      legal: 'Rechtliches',
      subscribe: 'Bleiben Sie Ihren Versorgungsdaten voraus',
      subscribeSub: 'Monatliche Einblicke zu Ausgabenmanagement, Zahlungen und Nachhaltigkeit. Kein Spam.',
      subscribePlaceholder: 'Geschäftliche E-Mail',
      subscribeCta: 'Abonnieren',
      subscribed: 'Abonniert — danke!',
      rights: 'Alle Rechte vorbehalten.',
      privacy: 'Datenschutz',
      terms: 'AGB',
      cookies: 'Cookies',
      sla: 'SLA',
      dpa: 'DPA',
      security: 'Sicherheit',
      disclaimer:
        'Fallstudien sind anonymisiert. Die Zahlen sind repräsentativ für verwaltete Portfolios und identifizieren keinen Kunden.',
    },
    form: {
      name: 'Vollständiger Name',
      email: 'Geschäftliche E-Mail',
      company: 'Unternehmen',
      phone: 'Telefon',
      jobTitle: 'Position',
      segment: 'Was beschreibt Sie am besten?',
      portfolioSize: 'Portfoliogröße',
      interest: 'Woran sind Sie am meisten interessiert?',
      message: 'Wie können wir helfen?',
      selectOption: 'Option wählen',
      namePh: 'Erika Mustermann',
      emailPh: 'erika@firma.com',
      companyPh: 'Firmenname',
      phonePh: '+1 (555) 000-0000',
      messagePh: 'Erzählen Sie uns von Ihrem Portfolio, Ihren Anbietern und Zielen…',
      consent: 'Mit dem Absenden stimmen Sie zu, zu BillPortal kontaktiert zu werden. Wir respektieren Ihre Privatsphäre.',
      successTitle: 'Danke — Anfrage erhalten',
      successBody: 'Unser Team meldet sich innerhalb eines Werktags, um Ihre Demo zu planen.',
      errorBody: 'Etwas ist schiefgelaufen. Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.',
      submitDemo: 'Meine Demo anfragen',
      submitContact: 'Nachricht senden',
    },
    demo: {
      title: 'Persönliche Demo anfragen',
      subtitle:
        'Sehen Sie BillPortal auf Ihr Portfolio abgebildet: Erfassung, Validierung, Zahlungen und Reporting in einem Arbeitsbereich.',
      benefits: [
        'Eine auf Ihr Segment und Ihre Anbieter zugeschnittene Demo',
        'Live-Einblick in KI-Parsing, Validierung und Zahlungsdateien',
        'Begleitung bei Onboarding, historischen Daten und Go-live',
      ],
    },
  },
};

export default translations;
